import { useWeb3 } from "@chainsafe/web3-context";
import React, { useContext, useEffect, useReducer, useState } from "react";
import { Bridge, BridgeFactory, Erc20 } from "@chainsafe/chainbridge-contracts";
import { PalmBridge } from "../Contracts/PalmBridge";
import { ERC721 } from "../Contracts/ERC721.d";
import { ERC1155 } from "../Contracts/ERC1155.d";
import { BridgeTollBooth } from "../Contracts/BridgeTollBooth";
import { PalmBridge__factory as PalmBridgeFactory } from "../Contracts/factories/PalmBridge__factory";
import { BridgeTollBooth__factory as TollBoothFactory } from "../Contracts/factories/BridgeTollBooth__factory";
import { ERC721__factory as ERC721Factory } from "../Contracts/factories/ERC721__factory";
import { ERC1155__factory as ERC1155Factory } from "../Contracts/factories/ERC1155__factory";
import { isPalmNetwork, TokenConfig1155 } from "../chainbridgeConfig";
import {
  BigNumber,
  BigNumberish,
  ContractTransaction,
  ethers,
  Overrides,
  PayableOverrides,
  utils,
} from "ethers";
import { Erc20DetailedFactory } from "../Contracts/Erc20DetailedFactory";
import {
  BridgeConfig,
  chainbridgeConfig,
  TokenConfig,
} from "../chainbridgeConfig";
import { transitMessageReducer } from "./Reducers/TransitMessageReducer";
import { Weth } from "../Contracts/Weth";
import { WethFactory } from "../Contracts/WethFactory";
import { Erc20Detailed } from "../Contracts/Erc20Detailed";
import { formatGatewayUri } from "../Utils/IpfsUriFormatter";
import {
  executeTransactionWithRetry,
  formatBigNumber,
  getGasPriceEstimate,
} from "./chainbridgeHelpers";

interface IChainbridgeContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export type Vote = {
  address: string;
  signed: "Confirmed" | "Rejected";
};

const resetAllowanceLogicFor = [
  "0xdac17f958d2ee523a2206206994597c13d831ec7", //USDT
  //Add other offending tokens here
];

export type NFTTokenDeclaration = TokenConfig &
  (
    | { contract: ERC721; type: "ERC721" }
    | { contract: ERC1155; type: "ERC1155" }
  );

export type ERC721Metadata = {
  name: string;
  description: string;
  image: string;
};

export type DepositParameters = {
  recipient: string;
  tokenAddress: string;
  amount?: number;
  tokenId?: string;
};

type AvailableTokenTypes = "ERC20" | "ERC721" | "ERC1155";
type ConfigTypeTuple = [TokenConfig | TokenConfig1155, AvailableTokenTypes];

type ChainbridgeContext = {
  homeChain?: BridgeConfig;
  destinationChain?: BridgeConfig;
  destinationChains: Array<{ chainId: number; name: string }>;
  setDestinationChain(chainId: number): void;
  getTokenType(token: Erc20 | ERC721 | ERC1155): AvailableTokenTypes;
  getAllTokenConfigs(): Array<ConfigTypeTuple>;
  deposit(params: DepositParameters): Promise<void>;
  resetDeposit(): void;
  transactionStatus?: TransactionStatus;
  depositVotes: number;
  relayerThreshold?: number;
  depositNonce?: string;
  inTransitMessages: Array<string | Vote>;
  depositAmount?: number;
  bridgeFee?: number;
  transferTxHash?: string;
  selectedToken?: string;
  wrapToken:
    | ((
        overrides?: PayableOverrides | undefined
      ) => Promise<ContractTransaction>)
    | undefined;
  unwrapToken:
    | ((
        wad: BigNumberish,
        overrides?: Overrides | undefined
      ) => Promise<ContractTransaction>)
    | undefined;
  wrapTokenConfig: TokenConfig | undefined;

  // Added Palm items
  homeChainIsPalm: boolean;
  palmRelayFee: BigNumber;
  palmGreenFee: BigNumber;
  totalPalmFees: BigNumber;
  tollBooth?: TollBoothFactory;
  tollTokenAddress?: string;
  tollToken?: Erc20Detailed;
  tollTokenSymbol?: string;
  tollTokenDecimals?: number;
  allowPalmBridgeFees: (gasPrice: number, gasLimit: number) => Promise<void>;

  // NFT bridge items
  nftTokens: Array<NFTTokenDeclaration>; // token config with intantiated contract
  getTotalTokensOwnedForNFT: (token: ERC721 | ERC1155) => Promise<number>;
  updateTokenIdsOwnedForNFT: (
    token: ERC721 | ERC1155,
    tokenIds?: Array<string>
  ) => Promise<void>;
  ownedTokenIdsByTokenAddress: Record<string, BigNumber[]>;
  updateMetadataForTokenId: (
    token: ERC721 | ERC1155,
    tokenId: BigNumber | string
  ) => Promise<void>;
  tokenMetadataByTokenAddressAndId: Record<
    string,
    Record<string, ERC721Metadata>
  >;
};

export type TransactionStatus =
  | "Paying Bridge Fees"
  | "Initializing Transfer"
  | "In Transit"
  | "Transfer Completed"
  | "Transfer Aborted";

const ChainbridgeContext = React.createContext<ChainbridgeContext | undefined>(
  undefined
);

const ChainbridgeProvider = ({ children }: IChainbridgeContextProps) => {
  const { isReady, network, provider, address, tokens } = useWeb3();
  const [gasPriceEstimate, setGasPriceEstimate] = useState(0);
  const [homeChain, setHomeChain] = useState<BridgeConfig | undefined>();
  const [relayerThreshold, setRelayerThreshold] = useState<number | undefined>(
    undefined
  );
  const [destinationChain, setDestinationChain] = useState<
    BridgeConfig | undefined
  >();
  const [destinationChains, setDestinationChains] = useState<BridgeConfig[]>(
    []
  );
  // Contracts
  const [homeBridge, setHomeBridge] = useState<Bridge | undefined>(undefined);
  const [wrapper, setWrapper] = useState<Weth | undefined>(undefined);
  const [wrapTokenConfig, setWrapperConfig] = useState<TokenConfig | undefined>(
    undefined
  );
  const [destinationBridge, setDestinationBridge] = useState<
    Bridge | undefined
  >(undefined);
  const [transactionStatus, setTransactionStatus] = useState<
    TransactionStatus | undefined
  >(undefined);
  const [depositNonce, setDepositNonce] = useState<string | undefined>(
    undefined
  );
  const [depositVotes, setDepositVotes] = useState<number>(0);
  const [inTransitMessages, tokensDispatch] = useReducer(
    transitMessageReducer,
    []
  );
  const [depositAmount, setDepositAmount] = useState<number | undefined>();
  const [bridgeFee, setBridgeFee] = useState<number | undefined>();
  const [transferTxHash, setTransferTxHash] = useState<string>("");
  const [selectedToken, setSelectedToken] = useState<string>("");

  // Palm data
  const [homeChainIsPalm, setHomeChainIsPalm] = useState<boolean>(false);
  const [, setPalmBridge] = useState<PalmBridge>();
  const [palmRelayFee, setPalmRelayFee] = useState<BigNumber>(
    BigNumber.from("0")
  );
  const [palmGreenFee, setPalmGreenFee] = useState<BigNumber>(
    BigNumber.from("0")
  );
  const [totalPalmFees, setTotalPalmFees] = useState<BigNumber>(
    BigNumber.from("0")
  );
  const [tollBoothAddress, setTollBoothAddress] = useState<string>();
  const [tollBooth, setTollBooth] = useState<BridgeTollBooth>();
  const [tollTokenAddress, setTollTokenAddress] = useState<string>();
  const [tollToken, setTollToken] = useState<Erc20Detailed>();
  const [tollTokenSymbol, setTollTokenSymbol] = useState<string>();
  const [tollTokenDecimals, setTollTokenDecimals] = useState<number>();

  // NFT Data
  const [nftTokens, setNFTTokens] = useState<NFTTokenDeclaration[]>([]);
  const [ownedTokenIdsByTokenAddress, setOwnedTokenIdsByTokenAddress] =
    useState<Record<string, BigNumber[]>>({});
  const [
    tokenMetadataByTokenAddressAndId,
    setTokenMetadataByTokenAddressAndId,
  ] = useState<Record<string, Record<string, ERC721Metadata>>>({});

  const resetDeposit = () => {
    chainbridgeConfig.chains.length > 2 && setDestinationChain(undefined);
    setTransactionStatus(undefined);
    setDepositNonce(undefined);
    setDepositVotes(0);
    setDepositAmount(undefined);
    tokensDispatch({
      type: "resetMessages",
    });
    setSelectedToken("");
  };

  const handleSetDestination = (chainId: number) => {
    const chain = destinationChains.find((c) => c.chainId === chainId);
    if (!chain) {
      throw new Error("Invalid destination chain selected");
    }
    setDestinationChain(chain);
  };

  const refreshGasEstimate = async () => {
    const estimate = await getGasPriceEstimate();
    setGasPriceEstimate(estimate);
  };

  const gasPricePollingInterval = 120;
  useEffect(() => {
    let poller: number;
    if (network === 1) {
      refreshGasEstimate();
      poller = setInterval(getGasPriceEstimate, gasPricePollingInterval * 1000);
    } else {
      setGasPriceEstimate(10);
    }
    return () => {
      if (poller) {
        clearInterval(poller);
      }
    };
  }, [network]);

  useEffect(() => {
    if (destinationChain) {
      let provider;
      if (destinationChain?.rpcUrl.startsWith("wss")) {
        if (destinationChain.rpcUrl.includes("infura")) {
          const parts = destinationChain.rpcUrl.split("/");

          provider = new ethers.providers.InfuraWebSocketProvider(
            destinationChain.networkId,
            parts[parts.length - 1]
          );
        }
        if (destinationChain.rpcUrl.includes("alchemyapi")) {
          const parts = destinationChain.rpcUrl.split("/");

          provider = new ethers.providers.AlchemyWebSocketProvider(
            destinationChain.networkId,
            parts[parts.length - 1]
          );
        }
      } else {
        provider = new ethers.providers.JsonRpcProvider(
          destinationChain?.rpcUrl
        );
      }
      if (provider) {
        const bridge = BridgeFactory.connect(
          destinationChain?.bridgeAddress,
          provider
        );
        setDestinationBridge(bridge);
      }
    }
  }, [destinationChain]);

  useEffect(() => {
    if (network && isReady) {
      const home = chainbridgeConfig.chains.find(
        (c) => c.networkId === network
      );
      if (!home) {
        setHomeChain(undefined);
        setHomeBridge(undefined);
        setWrapperConfig(undefined);
        setWrapper(undefined);
        return;
      }
      setHomeChain(home);

      const signer = provider?.getSigner();
      if (!signer) {
        console.log("No signer");
        return;
      }

      const bridge = BridgeFactory.connect(home.bridgeAddress, signer);
      setHomeBridge(bridge);
      setDestinationChains(
        chainbridgeConfig.chains.filter((c) => c.networkId !== network)
      );
      if (chainbridgeConfig.chains.length === 2) {
        const destChain = chainbridgeConfig.chains.find(
          (c) => c.networkId !== network
        );

        destChain && setDestinationChain(destChain);
      }

      const wrapperToken = home.tokens.find(
        (token) => token.isNativeWrappedToken
      );

      if (!wrapperToken) {
        setWrapperConfig(undefined);
        setWrapper(undefined);
      } else {
        setWrapperConfig(wrapperToken);
        const connectedWeth = WethFactory.connect(wrapperToken.address, signer);
        setWrapper(connectedWeth);
      }
    } else {
      setHomeChain(undefined);
      setWrapperConfig(undefined);
      setWrapper(undefined);
    }
    resetDeposit();
  }, [isReady, network, provider]);

  useEffect(() => {
    const getRelayerThreshold = async () => {
      if (homeBridge) {
        const threshold = BigNumber.from(
          await homeBridge._relayerThreshold()
        ).toNumber();
        setRelayerThreshold(threshold);
      }
    };
    const getBridgeFee = async () => {
      if (homeBridge) {
        const bridgeFee = Number(utils.formatEther(await homeBridge._fee()));
        setBridgeFee(bridgeFee);
      }
    };
    getRelayerThreshold();
    getBridgeFee();
  }, [homeBridge]);

  useEffect(() => {
    if (homeChain && destinationBridge && depositNonce) {
      destinationBridge.on(
        destinationBridge.filters.ProposalEvent(
          homeChain.chainId,
          BigNumber.from(depositNonce),
          null,
          null,
          null
        ),
        (originChainId, depositNonce, status, resourceId, dataHash, tx) => {
          switch (BigNumber.from(status).toNumber()) {
            case 1:
              tokensDispatch({
                type: "addMessage",
                payload: `Proposal created on ${destinationChain?.name}`,
              });
              break;
            case 2:
              tokensDispatch({
                type: "addMessage",
                payload: `Proposal has passed. Executing...`,
              });
              break;
            case 3:
              setTransactionStatus("Transfer Completed");
              setTransferTxHash(tx.transactionHash);
              break;
            case 4:
              setTransactionStatus("Transfer Aborted");
              setTransferTxHash(tx.transactionHash);
              break;
          }
        }
      );

      destinationBridge.on(
        destinationBridge.filters.ProposalVote(
          homeChain.chainId,
          BigNumber.from(depositNonce),
          null,
          null
        ),
        async (originChainId, depositNonce, status, resourceId, tx) => {
          const txReceipt = await tx.getTransactionReceipt();
          if (txReceipt.status === 1) {
            setDepositVotes(depositVotes + 1);
          }
          tokensDispatch({
            type: "addMessage",
            payload: {
              address: String(txReceipt.from),
              signed: txReceipt.status === 1 ? "Confirmed" : "Rejected",
            },
          });
        }
      );
    }
    return () => {
      //@ts-ignore
      destinationBridge?.removeAllListeners();
    };
  }, [
    depositNonce,
    homeChain,
    destinationBridge,
    depositVotes,
    destinationChain,
    inTransitMessages,
  ]);

  const getAllTokenConfigs = (): Array<ConfigTypeTuple> => {
    if (!homeBridge || !homeChain) {
      console.log("Home bridge contract is not instantiated");
      return [];
    }

    return [
      ...homeChain.tokens.map<ConfigTypeTuple>((tokenConfig) => [
        tokenConfig,
        "ERC20",
      ]),
      ...homeChain.erc721Tokens.map<ConfigTypeTuple>((tokenConfig) => [
        tokenConfig,
        "ERC721",
      ]),
      ...homeChain.erc1155Tokens.map<ConfigTypeTuple>((tokenConfig) => [
        tokenConfig,
        "ERC1155",
      ]),
    ];
  };

  const getTokenType = (
    token: Erc20 | ERC721 | ERC1155
  ): AvailableTokenTypes => {
    if (!homeBridge || !homeChain) {
      throw new Error("Home bridge contract is not instantiated");
    }

    let matchingTokenTuple: ConfigTypeTuple | undefined =
      getAllTokenConfigs().find((tuple) => tuple[0].address === token.address);

    if (matchingTokenTuple == undefined) {
      throw new Error("Could not find token for addres " + token.address);
    }

    return matchingTokenTuple[1];
  };

  const deposit = async ({
    recipient,
    tokenAddress,
    amount,
    tokenId,
  }: {
    recipient: string;
    tokenAddress: string;
    amount?: number;
    tokenId?: string;
  }) => {
    if (!homeBridge || !homeChain) {
      console.log("Home bridge contract is not instantiated");
      return;
    }

    if (!destinationChain || !destinationBridge) {
      console.log("Destination bridge contract is not instantiated");
      return;
    }

    const signer = provider?.getSigner();
    if (!address || !signer) {
      console.log("No signer");
      return;
    }

    // Try finding the token in the ERC20 list first
    let allTokensConfigs = getAllTokenConfigs();

    let matchingTokenTuple: ConfigTypeTuple | undefined = allTokensConfigs.find(
      (token) => token[0].address === tokenAddress
    );

    if (matchingTokenTuple == undefined) {
      console.log("Invalid token selected");
      return;
    }

    const token = matchingTokenTuple[0];
    const tokenType = matchingTokenTuple[1];

    const isErc20 = tokenType == "ERC20";
    const isErc721 = tokenType == "ERC721";
    const isErc1155 = tokenType == "ERC1155";

    // `data` is the bytes data being sent to the bridge's deposit function
    let data = "";
    const gasPrice = homeChain.defaultGasPrice || gasPriceEstimate;
    const depositGasLimit = isErc20 ? 350000 : 515000;
    const approvalGasLimit = 52000;

    try {
      if (homeChainIsPalm) {
        setTransactionStatus("Paying Bridge Fees");

        // Note: allowPalmBridgeFees is defined below.
        await allowPalmBridgeFees(gasPrice, approvalGasLimit);
      }

      setTransactionStatus("Initializing Transfer");
      setSelectedToken(tokenAddress);

      if (isErc20) {
        if (amount == undefined) {
          console.log(
            "Missing expected parameter 'amount' from deposit parameters for ERC20"
          );
          return;
        }

        setDepositAmount(amount);

        const erc20 = Erc20DetailedFactory.connect(tokenAddress, signer);
        const erc20Decimals = tokens[tokenAddress].decimals;

        data =
          "0x" +
          utils
            .hexZeroPad(
              // TODO Wire up dynamic token decimals
              formatBigNumber(amount || 0, erc20Decimals).toHexString(),
              32
            )
            .substr(2) + // Deposit Amount (32 bytes)
          utils
            .hexZeroPad(utils.hexlify((recipient.length - 2) / 2), 32)
            .substr(2) + // len(recipientAddress) (32 bytes)
          recipient.substr(2); // recipientAddress (?? bytes)

        const currentAllowance = await erc20.allowance(
          address,
          homeChain.erc20HandlerAddress
        );

        if (
          Number(utils.formatUnits(currentAllowance, erc20Decimals)) < amount
        ) {
          if (
            Number(utils.formatUnits(currentAllowance, erc20Decimals)) > 0 &&
            resetAllowanceLogicFor.includes(tokenAddress)
          ) {
            //We need to reset the user's allowance to 0 before we give them a new allowance
            //TODO Should we alert the user this is happening here?
            const resetAllowanceTx = (options: Overrides) =>
              erc20.approve(
                homeChain.erc20HandlerAddress,
                formatBigNumber("0", erc20Decimals),
                options
              );
            await (
              await executeTransactionWithRetry(
                { gasPrice, gasLimit: approvalGasLimit },
                resetAllowanceTx
              )
            ).wait(1);
          }

          const approveAmountTx = (options: Overrides) =>
            erc20.approve(
              homeChain.erc20HandlerAddress,
              formatBigNumber(amount, erc20Decimals),
              options
            );
          await (
            await executeTransactionWithRetry(
              { gasPrice, gasLimit: approvalGasLimit },
              approveAmountTx
            )
          ).wait(1);
        }
      } else if (isErc721) {
        // Handle 721 transfer
        const id = BigNumber.from(tokenId);
        const erc721 = ERC721Factory.connect(tokenAddress, signer);
        data =
          "0x" +
          ethers.utils.hexZeroPad(ethers.utils.hexlify(id), 32).substr(2) + // Deposit Amount        (32 bytes)
          ethers.utils
            .hexZeroPad(ethers.utils.hexlify((recipient.length - 2) / 2), 32)
            .substr(2) + // len(recipientAddress) (32 bytes)
          ethers.utils.hexlify(recipient).substr(2); // recipientAddress      (?? bytes)

        const approved = await erc721.getApproved(String(id));

        const approve721TransferTx = (options: Overrides) =>
          erc721.approve(homeChain.erc721HandlerAddress, String(id), options);

        if (approved !== homeChain.erc721HandlerAddress) {
          await (
            await executeTransactionWithRetry(
              { gasPrice, gasLimit: approvalGasLimit },
              approve721TransferTx
            )
          ).wait(1);
        }
      } else if (isErc1155) {
        // Handle 1155 transfer
        const id = BigNumber.from(tokenId);
        const erc1155 = ERC1155Factory.connect(tokenAddress, signer);

        // We're going to hardcode the amount to transfer to 1 for now
        const amount = 1;

        // Needed for 1155's. We're going to leave it empty for now.
        const extradata = "0x";

        data =
          "0x" +
          ethers.utils.hexZeroPad(ethers.utils.hexlify(id), 32).substr(2) + // Token id (32 bytes)
          ethers.utils.hexZeroPad(ethers.utils.hexlify(amount), 32).substr(2) + // Deposit Amount (32 bytes)
          ethers.utils
            .hexZeroPad(ethers.utils.hexlify((recipient.length - 2) / 2), 32)
            .substr(2) + // len(recipientAddress) (32 bytes)
          ethers.utils
            .hexZeroPad(ethers.utils.hexlify((extradata.length - 2) / 2), 32)
            .substr(2) + // len(recipientAddress) (32 bytes)
          ethers.utils.hexlify(recipient).substr(2) + // recipientAddress      (?? bytes)
          ethers.utils.hexlify(extradata).substr(2); // extradata             (?? bytes)

        const approved = await erc1155.isApprovedForAll(
          address,
          homeChain.erc1155HandlerAddress
        );

        const approve1155TransferTx = (options: Overrides) =>
          erc1155.setApprovalForAll(
            homeChain.erc1155HandlerAddress,
            true,
            options
          );

        if (approved !== true) {
          await (
            await executeTransactionWithRetry(
              { gasPrice, gasLimit: approvalGasLimit },
              approve1155TransferTx
            )
          ).wait(1);
        }
      }

      homeBridge.once(
        homeBridge.filters.Deposit(
          destinationChain.chainId,
          token.resourceId,
          null
        ),
        (destChainId, resourceId, depositNonce) => {
          setDepositNonce(`${depositNonce.toString()}`);
          setTransactionStatus("In Transit");
        }
      );

      const depositTx = (options: Overrides) => {
        const value = formatBigNumber(bridgeFee || 0, 18);
        return homeBridge.deposit(
          destinationChain.chainId,
          token.resourceId,
          data,
          { ...options, value }
        );
      };
      await (
        await executeTransactionWithRetry(
          { gasPrice, gasLimit: depositGasLimit },
          depositTx
        )
      ).wait(1);
      return Promise.resolve();
    } catch (error) {
      console.error(error);
      setTransactionStatus("Transfer Aborted");
      return Promise.reject();
    }
  };

  // Palm specific effects and functions
  useEffect(() => {
    if (!homeChain || !network) {
      return;
    }

    const isPalm = isPalmNetwork(network);

    setHomeChainIsPalm(isPalm);

    // Only run the effects if we are on the palm network, and if the network and homeChain are aligned
    // (this sometimes gets called before the two variables are in sync).
    if (!isPalm || homeChain.networkId != network) {
      return;
    }

    const signer = provider?.getSigner();
    if (!address || !signer) {
      console.log("No signer");
      return;
    }

    const bridge = PalmBridgeFactory.connect(homeChain.bridgeAddress, signer);

    setPalmBridge(bridge);

    let fetchFeeData = async () => {
      let tollBoothAddress = await bridge.tollBooth();

      setTollBoothAddress(tollBoothAddress);

      let tollBooth = TollBoothFactory.connect(tollBoothAddress, signer);

      setTollBooth(tollBooth);

      let fees = await tollBooth.getFees();

      setPalmRelayFee(fees.relayFee);
      setPalmGreenFee(fees.greenFee);

      let totalFees = await tollBooth.totalFees();

      setTotalPalmFees(totalFees);

      let tollTokenAddress = await tollBooth.tollToken();

      setTollTokenAddress(tollTokenAddress);

      const tollToken = Erc20DetailedFactory.connect(tollTokenAddress, signer);

      setTollToken(tollToken);

      let symbol = await tollToken.symbol();
      let decimals = await tollToken.decimals();

      setTollTokenSymbol(symbol);
      setTollTokenDecimals(decimals);
    };

    fetchFeeData();
  }, [homeChain, network]);

  let allowPalmBridgeFees = async (gasPrice: number, gasLimit: number) => {
    if (!homeChainIsPalm) {
      throw new Error(
        "allowBridgeFees() can only be run against the Palm bridge"
      );
    }

    if (!tollToken) {
      throw new Error(
        "cannot run allowBridgeFees(): tollToken not yet initialized"
      );
    }

    if (!tollBoothAddress) {
      throw new Error(
        "cannot run allowBridgeFees(): tollBoothAddress not yet initialized"
      );
    }

    if (!tollBooth) {
      throw new Error(
        "cannot run allowBridgeFees(): tollBooth not yet initialized"
      );
    }

    let totalFees = await tollBooth.totalFees();
    if (!totalFees) {
      throw new Error("cannot run allowBridgeFees(): totalFees not loaded");
    }

    setTotalPalmFees(totalFees);

    const tollApprovalTx = (options: Overrides) =>
      tollToken.approve(tollBoothAddress, totalFees, options);
    await (
      await executeTransactionWithRetry({ gasPrice, gasLimit }, tollApprovalTx)
    ).wait(1);
  };

  // NFT specific effects and functions

  type NFTTokenType = "ERC721" | "ERC1155";

  useEffect(() => {
    if (!homeChain || !network) {
      return;
    }

    const signer = provider?.getSigner();
    if (!address || !signer) {
      console.log("No signer");
      return;
    }

    let tokenArray: Array<NFTTokenDeclaration> = [];

    (homeChain.erc721Tokens || []).forEach((tokenConfig) => {
      tokenArray.push({
        ...tokenConfig,
        type: "ERC721",
        contract: ERC721Factory.connect(tokenConfig.address, signer),
      });
    });

    (homeChain.erc1155Tokens || []).forEach((tokenConfig) => {
      tokenArray.push({
        ...tokenConfig,
        type: "ERC1155",
        contract: ERC1155Factory.connect(tokenConfig.address, signer),
      });
    });

    setNFTTokens(tokenArray);
  }, [homeChain, network]);

  let updateTokenIdsOwnedForNFT = async (token: ERC721 | ERC1155) => {
    if (!address) {
      throw new Error("Not yet connected to wallet; no address.");
    }

    let totalTokensOwned = await getTotalTokensOwnedForNFT(token);
    let tokenIds: Array<BigNumber> = [];
    let tokenType = getTokenType(token);

    if (tokenType == "ERC721") {
      for (var index = 0; index < totalTokensOwned; index++) {
        tokenIds.push(
          await (token as ERC721).tokenOfOwnerByIndex(address, index)
        );
      }
    } else {
      // TODO: ERC 1155
      // Get the id list from the config
      let allAvailableTokenIds: Array<string> =
        homeChain?.erc1155Tokens.filter((tokenConfig) => {
          return tokenConfig.address == token.address;
        })[0].tokenIds || [];

      let tokenIdsAsHex = allAvailableTokenIds.map((id) => {
        return BigInt(id);
      });

      // For now, we only report which token ids are owned, not the amount of each token id.
      // We'll leave reporting that for a later version.
      let response = await (token as ERC1155).balanceOfBatch(
        new Array(tokenIdsAsHex.length).fill(address),
        tokenIdsAsHex
      );

      response.forEach((amount, index) => {
        if (amount.gt(BigNumber.from(0))) {
          tokenIds.push(BigNumber.from(tokenIdsAsHex[index]));
        }
      });
    }

    setOwnedTokenIdsByTokenAddress({
      ...ownedTokenIdsByTokenAddress,
      [token.address]: tokenIds,
    });
  };

  let getTotalTokensOwnedForNFT = async (token: ERC721 | ERC1155) => {
    if (!address) {
      throw new Error("Not yet connected to wallet; no address.");
    }

    let balance = 0;
    let tokenType = getTokenType(token);

    if (tokenType == "ERC721") {
      let response = await (token as ERC721).balanceOf(address);
      balance = response.toNumber();
    } else if (tokenType == "ERC1155") {
      // Get the id list from the config
      let allAvailableTokenIds: Array<string> =
        homeChain?.erc1155Tokens.filter((tokenConfig) => {
          return tokenConfig.address == token.address;
        })[0].tokenIds || [];

      let tokenIdsAsHex = allAvailableTokenIds.map((id) => {
        return BigInt(id);
      });

      let response = await (token as ERC1155).balanceOfBatch(
        new Array(tokenIdsAsHex.length).fill(address),
        tokenIdsAsHex
      );
      response.forEach((amount, index) => {
        if (amount.gt(BigNumber.from(0))) {
          balance++;
        }
      });
    } else {
      throw new Error(
        "FATAL ERROR: Unknown token type! Not ERC721 and not ERC1155!"
      );
    }

    return balance;
  };

  let updateMetadataForTokenId = async (
    token: ERC721 | ERC1155,
    tokenId: BigNumber | string
  ) => {
    if (!chainbridgeConfig.ipfsGateway) {
      throw new Error("IPFS gateway not configured.");
    }

    const ipfsGateway = chainbridgeConfig.ipfsGateway;
    let tokenURI;
    let tokenType = getTokenType(token);

    if (tokenType == "ERC721") {
      tokenURI = await (token as ERC721).tokenURI(BigNumber.from(tokenId));
    } else if (tokenType == "ERC1155") {
      tokenURI = await (token as ERC1155).uri(BigNumber.from(tokenId));
    } else {
      throw new Error("FATAL ERROR: Unknown token type for `token` variable!");
    }

    tokenURI = formatGatewayUri(tokenURI, ipfsGateway);

    let response;
    let metadata: ERC721Metadata = {
      name: "⚠️ Failed to fetch metadata",
      description: "",
      image: "",
    };
    try {
      response = tokenURI ? await fetch(tokenURI) : undefined;
      const metadataJson = (await response?.json()) as ERC721Metadata;
      if (metadataJson) {
        metadata = metadataJson;
      }
    } catch (e) {
      console.log("Failed to fetch token metadata");
      console.error(e);
    }

    // Rewrite image uri
    if (metadata.image) {
      metadata.image = formatGatewayUri(metadata.image, ipfsGateway);
    }

    setTokenMetadataByTokenAddressAndId({
      ...tokenMetadataByTokenAddressAndId,
      [token.address]: {
        ...tokenMetadataByTokenAddressAndId[token.address],
        [tokenId.toString()]: metadata,
      },
    });
  };

  return (
    <ChainbridgeContext.Provider
      value={{
        homeChain: homeChain,
        destinationChain: destinationChain,
        destinationChains: destinationChains.map((c) => ({
          chainId: c.chainId,
          name: c.name,
        })),
        setDestinationChain: handleSetDestination,
        getTokenType,
        getAllTokenConfigs,
        deposit,
        resetDeposit,
        depositVotes,
        relayerThreshold: relayerThreshold,
        depositNonce,
        bridgeFee,
        transactionStatus,
        inTransitMessages,
        depositAmount,
        transferTxHash,
        selectedToken,
        wrapToken: wrapper?.deposit,
        wrapTokenConfig,
        unwrapToken: wrapper?.withdraw,

        // Palm specific
        homeChainIsPalm,
        palmRelayFee,
        palmGreenFee,
        totalPalmFees,
        tollToken,
        tollTokenAddress,
        tollTokenSymbol,
        tollTokenDecimals,
        allowPalmBridgeFees,

        // NFT bridge items
        nftTokens,
        getTotalTokensOwnedForNFT,
        updateTokenIdsOwnedForNFT,
        ownedTokenIdsByTokenAddress,
        updateMetadataForTokenId,
        tokenMetadataByTokenAddressAndId,
      }}
    >
      {children}
    </ChainbridgeContext.Provider>
  );
};

const useChainbridge = () => {
  const context = useContext(ChainbridgeContext);
  if (context === undefined) {
    throw new Error("useChainbridge must be called within a DriveProvider");
  }
  return context;
};

export { ChainbridgeProvider, useChainbridge };
